import { accountUrl } from '@/lib/config'
import type { ReactNode } from 'react'

export function PlanCard({
  title,
  description,
  price,
  note,
  features,
  cta,
  badge,
  featured = false,
  dark = false,
  businessTone,
  control,
}: {
  title: string
  description: string
  price: string
  note: string
  features: string[]
  cta: string
  badge?: string
  featured?: boolean
  dark?: boolean
  businessTone?: 'single' | 'starter' | 'professional' | 'enterprise'
  control?: ReactNode
}) {
  const isBusinessCard = Boolean(businessTone)
  const businessToneClass = businessTone === 'single'
    ? 'border-[#D7D9D7] bg-gradient-to-br from-[#F4F8FF] to-[#EDF5F2]'
    : businessTone === 'starter'
      ? 'border-[#D7D9D7] bg-gradient-to-br from-[#FFF9F1] to-[#F4F8FF]'
      : businessTone === 'professional'
        ? 'border-[#B8D3BE] bg-gradient-to-br from-[#F0F8F1] to-[#F7FBFF]'
        : businessTone === 'enterprise'
          ? 'border-[#D7D9D7] bg-gradient-to-br from-[#F7F7F6] to-[#EEF1F0]'
          : ''

  return (
    <article className={`relative flex h-full flex-col rounded-[18px] border p-7 shadow-[0_14px_28px_rgba(20,35,28,0.08)] md:p-8 ${dark ? 'border-white/10 bg-[#2B0A4A] text-white' : businessToneClass || 'border-[#E9DDCE] bg-white text-[#1B0044]'} ${featured && !dark ? 'border-2 border-[#2B0A4A] shadow-[0_18px_45px_rgba(43,10,74,0.14)]' : ''}`}>
      {badge && (
        <span className={`absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.08em] ${dark ? 'bg-[#FFC933] text-[#1B0044]' : 'bg-[#FFC933] text-[#1B0044]'}`}>
          {badge}
        </span>
      )}
      <div className={isBusinessCard ? 'h-[11rem] shrink-0' : 'min-h-[11rem]'}>
        <h3 className={`mt-1 text-3xl font-bold tracking-[-0.04em] ${dark ? 'text-white' : 'text-[#1B0044]'}`}>{title}</h3>
        <p className={`mt-3 text-sm leading-relaxed ${dark ? 'text-white/80' : 'text-[#716776]'}`}>{description}</p>
      </div>
      {isBusinessCard && (
        <div style={{ height: 128, flexShrink: 0 }}>
          {control}
        </div>
      )}
      <div className={`${isBusinessCard ? 'h-[68px] shrink-0' : 'min-h-[68px]'} mt-3 text-3xl font-extrabold tracking-[-0.04em] ${dark ? 'text-white' : 'text-[#1B0044]'}`}>
        {price}
        <small className={`mt-1 block text-xs font-medium tracking-normal ${dark ? 'text-white/70' : 'text-[#786E7B]'}`}>{note}</small>
      </div>
      {!businessTone && control}
      <div className={`${isBusinessCard ? 'h-12 shrink-0' : 'min-h-12'} mt-6`}>
        <a
          href={cta === 'Contact Sales' ? 'mailto:sales@vibevite.com' : isBusinessCard ? `${accountUrl}/app/business/signup` : `${accountUrl}/app/auth/register`}
          className={`inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl px-5 text-sm font-semibold transition ${featured || dark ? 'bg-[#FFC933] text-[#1B0044] hover:opacity-90' : 'border border-[#2B0A4A] bg-white text-[#2B0A4A] hover:bg-[#FFF8EC]'}`}
        >
          {cta} <span aria-hidden="true">→</span>
        </a>
      </div>
      <p className={`mt-7 h-3 text-[10px] font-extrabold uppercase tracking-[0.1em] ${dark ? 'text-white/70' : 'text-[#7E7181]'}`}>What&apos;s included</p>
      <ul className={`mt-4 flex min-h-[180px] flex-col gap-3 text-sm ${dark ? 'text-white/85' : 'text-[#5F5564]'}`}>
        {features.map((feature) => (
          <li key={feature} className="flex items-start gap-2.5">
            <span className={`mt-0.5 ${dark ? 'text-[#FFC933]' : 'text-[#2B0A4A]'}`}>✓</span>
            <span>{feature}</span>
          </li>
        ))}
      </ul>
    </article>
  )
}
